/**
 * Central English / Hinglish dictionary.
 *
 * Rules for editing this file:
 *  - `en` is the source of truth and the fallback. Every key MUST exist here.
 *  - `hiEn` is a Partial<> of `en`. A missing key silently falls back to the
 *    full English sentence — never a key, never a blank, never half-and-half.
 *  - Hinglish is natural Roman-script Hindi-English as Indian shoppers speak
 *    it: warm, simple, respectful. No formal/shudh Hindi, no slang.
 *  - NEVER translate: product names, trademarks, ingredient names and forms,
 *    quantities, doses, units, prices, discounts, pack sizes, delivery dates,
 *    certification/licence names, study titles, authors, journals, citations.
 *  - NEVER strengthen a claim. "supports" stays "support karta hai" — never
 *    "theek karta hai" / "cure".
 *  - Placeholders use {name} and must appear in both languages.
 */

import { SECTIONS_EN } from "./sections";

const baseEn = {
  // ---------- language control ----------
  "lang.label": "Language",
  "lang.english": "English",
  "lang.hinglish": "Hinglish",
  "lang.switchTo": "Switch site language to {lang}",

  // ---------- primary navigation ----------
  "nav.shop": "Shop",
  "nav.kids": "Kids",
  "nav.kidsFull": "TruWe Kids",
  "nav.quiz": "Find your routine",
  "nav.dietPlan": "Free diet plan",
  "nav.ingredients": "Ingredients",
  "nav.library": "Library",
  "nav.about": "About",
  "nav.contact": "Contact",
  "nav.loyalty": "Loyalty Rewards",
  "nav.account": "Account",
  "nav.cart": "Cart",
  "nav.home": "Home",
  "nav.primary": "Primary",
  "nav.cartItems": "{count} items in cart",
  "nav.openCart": "Open cart",
  "nav.openMenu": "Open menu",
  "nav.closeMenu": "Close menu",
  "nav.menu": "Menu",

  // menu subtitles
  "menu.sub.shop": "All products",
  "menu.sub.kids": "For little ones",
  "menu.sub.quiz": "Personalised quiz",
  "menu.sub.dietPlan": "7-day plan & routine",
  "menu.sub.ingredients": "Science & research",
  "menu.sub.library": "Articles & guides",
  "menu.sub.loyalty": "Earn TruCoins",
  "menu.sub.about": "Our story",
  "menu.sub.contact": "We're here to help",

  // ---------- search ----------
  "search.placeholder": "Search products & pages",
  "search.aria": "Search products and pages",
  "search.clear": "Clear search",
  "search.products": "Products",
  "search.pages": "Pages",
  "search.noMatches": 'No matches for "{query}"',
  "search.tryHint": 'Try "magnesium", "PCOS", or "ingredients".',

  // ---------- common ----------
  "common.loading": "Loading…",
  "common.pleaseWait": "Please wait…",
  "common.saving": "Saving…",
  "common.retry": "Try again",
  "common.close": "Close",
  "common.cancel": "Cancel",
  "common.continue": "Continue",
  "common.back": "Back",
  "common.next": "Next",
  "common.submit": "Submit",
  "common.done": "Done",
  "common.optional": "optional",
  "common.required": "required",
  "common.readMore": "Read more",
  "common.viewAll": "View all",
  "common.learnMore": "Learn more",
  "common.somethingWentWrong": "Something went wrong. Please try again.",
  "common.networkError": "Network problem. Please check your connection and try again.",
  "common.nothingHere": "Nothing here yet.",
  "common.copied": "Copied",

  // ---------- product / PDP ----------
  "pdp.choosePack": "Choose your pack",
  "pdp.howToTake": "How to take it",
  "pdp.whoIsItFor": "Who is it for?",
  "pdp.viewIngredients": "View ingredients",
  "pdp.seeTheData": "See the data",
  "pdp.addToCart": "Add to Cart",
  "pdp.buyNow": "Buy Now",
  "pdp.preReserve": "Pre-Reserve",
  "pdp.notifyMe": "Notify me",
  "pdp.launchingSoon": "Launching soon",
  "pdp.inStock": "In stock",
  "pdp.soldOut": "Sold out",
  "pdp.description": "Description",
  "pdp.optionalAddOn": "Optional add-on",
  "pdp.addBoth": "Add both",
  "pdp.deliveryAndPayment": "Delivery & payment",
  "pdp.enterPincode": "Enter pincode",
  "pdp.check": "Check",
  "pdp.deliveredIn": "Delivered in 2-4 days",
  "pdp.notServiceable": "Sorry, we don't deliver to this pincode yet.",
  "pdp.freeShipping": "Free shipping on every order",
  "pdp.labTested": "100%  Lab Tested",
  "pdp.secureCheckout": "Secure checkout",
  "pdp.codAvailable": "COD available",
  "pdp.prepaid": "Prepaid",
  "pdp.cashOnDelivery": "Cash on delivery",
  "pdp.studiedEffects": "What the research studied",
  "pdp.absorptionTitle": "Absorption, measured",
  "pdp.researchNote":
    "Research below is on the individual ingredients and their studied forms, not on the finished product.",
  "pdp.supportNote": "Supports your routine. Not a medicine and not a treatment for any disease.",

  // ---------- shop / collections ----------
  "shop.title": "Shop",
  "shop.allProducts": "All products",
  "shop.filterByConcern": "Filter by concern",
  "shop.noProducts": "No products match this filter yet.",
  "shop.viewProduct": "View product",
  "shop.from": "From",

  // ---------- cart ----------
  "cart.title": "Your cart",
  "cart.empty": "Your cart is empty",
  "cart.emptyHint": "Add a pack to get started.",
  "cart.startShopping": "Start shopping",
  "cart.subtotal": "Subtotal",
  "cart.discount": "Discount",
  "cart.shipping": "Shipping",
  "cart.free": "Free",
  "cart.total": "Total",
  "cart.checkout": "Continue to checkout",
  "cart.remove": "Remove",
  "cart.quantity": "Quantity",
  "cart.applyCoupon": "Apply coupon",
  "cart.couponPlaceholder": "Coupon code",
  "cart.couponApplied": "Coupon applied",
  "cart.couponInvalid": "This coupon isn't valid for your cart.",
  "cart.useTrucoins": "Use TruCoins",
  "cart.trucoinsError": "Couldn't apply TruCoins. Please try again.",
  "cart.updating": "Updating cart…",

  // ---------- checkout / order ----------
  "checkout.title": "Checkout",
  "checkout.contactDetails": "Contact details",
  "checkout.shippingAddress": "Shipping address",
  "checkout.paymentMethod": "Payment method",
  "checkout.payNow": "Pay now",
  "checkout.placeOrder": "Place order",
  "checkout.codFee": "COD advance (paid online)",
  "checkout.hostedNotice":
    "The payment screen is provided by our payment partner and may appear in English.",
  "checkout.orderPlaced": "Order placed",
  "checkout.thankYou": "Thank you for your order",
  "checkout.orderNumber": "Order number",
  "checkout.trackOrder": "Track order",
  "checkout.confirmationSent": "We've sent the confirmation on WhatsApp.",

  // ---------- account / auth ----------
  "auth.signIn": "Sign in",
  "auth.signOut": "Sign out",
  "auth.signUp": "Create account",
  "auth.mobileNumber": "Mobile number",
  "auth.mobileOptional": "Mobile number — optional",
  "auth.email": "Email",
  "auth.emailOptional": "Email — optional",
  "auth.name": "Name",
  "auth.sendOtp": "Send OTP",
  "auth.enterOtp": "Enter OTP",
  "auth.otpSent": "We've sent an OTP on WhatsApp.",
  "auth.otpHint": "Enter the 6-digit code we sent you.",
  "auth.resendOtp": "Resend OTP",
  "auth.verify": "Verify",
  "auth.invalidOtp": "That code isn't right. Please check and try again.",
  "auth.otpExpired": "This code has expired. Please request a new one.",
  "auth.continueWithGoogle": "Continue with Google",
  "auth.continueWithApple": "Continue with Apple",
  "account.title": "My account",
  "account.orders": "My orders",
  "account.noOrders": "You haven't placed an order yet.",
  "account.profile": "Profile",
  "account.addresses": "Addresses",
  "account.trucoins": "TruCoins",
  "account.referrals": "Referrals",

  // ---------- track order ----------
  "track.title": "Track your order",
  "track.placeholder": "Order number or mobile number",
  "track.cta": "Track order",
  "track.notFound": "We couldn't find that order. Please check the details.",
  "track.status": "Status",

  // ---------- quiz / routine finder / diet plan ----------
  "quiz.title": "Find your routine",
  "quiz.start": "Start the quiz",
  "quiz.question": "Question {current} of {total}",
  "quiz.selectOne": "Please choose one option to continue.",
  "quiz.seeResults": "See my routine",
  "quiz.results": "Your routine",
  "diet.title": "Free diet plan",
  "diet.cta": "Get my diet plan",
  "diet.generating": "Building your plan…",
  "diet.medicalNote":
    "This plan is general wellness guidance, not medical advice. If you are pregnant, breastfeeding, on medication or under treatment, please check with your doctor first.",
  "diet.conditionsLabel": "Any existing medical conditions?",

  // ---------- forms / validation ----------
  "form.requiredField": "This field is required.",
  "form.invalidMobile": "Please enter a valid 10-digit mobile number.",
  "form.invalidEmail": "Please enter a valid email address.",
  "form.invalidPincode": "Please enter a valid 6-digit pincode.",
  "form.consentRequired": "Please accept to continue.",
  "form.submitted": "Thank you — we've received your details.",
  "form.submitFailed": "We couldn't submit that. Please try again.",

  // ---------- reservations / notify / popups ----------
  "reserve.title": "Pre-reserve your pack",
  "reserve.subtitle": "Reserve now and we'll message you the moment it ships.",
  "reserve.cta": "Reserve my pack",
  "reserve.success": "Reserved. We'll message you on WhatsApp.",
  "notify.title": "Get a launch alert",
  "notify.subtitle": "We'll message you as soon as this is back in stock.",
  "notify.cta": "Notify me",
  "notify.success": "Done. We'll message you when it's available.",
  "popup.whatsappTitle": "Get order updates on WhatsApp",
  "popup.whatsappCta": "Join on WhatsApp",
  "popup.dismiss": "Not now",

  // ---------- consent ----------
  "consent.title": "Cookies & privacy",
  "consent.body":
    "We use cookies to run the store and to understand what's useful. You can change this anytime.",
  "consent.accept": "Accept",
  "consent.manage": "Manage settings",
  "consent.savePreferences": "Save preferences",

  // ---------- blog / library / ingredients ----------
  "blog.title": "Library",
  "blog.subtitle": "Research-backed guides on sleep, hormones, liver and gut health.",
  "blog.readTime": "{minutes} min read",
  "blog.relatedReading": "Related reading",
  "blog.references": "References",
  "blog.noArticles": "No articles here yet.",
  "ingredients.title": "Ingredients",
  "ingredients.subtitle": "Every active, its form, its dose and the research behind it.",
  "ingredients.form": "Form",
  "ingredients.dose": "Dose",
  "ingredients.studiedFor": "Studied for",

  // ---------- loyalty / referral / affiliate ----------
  "loyalty.title": "TruCoins",
  "loyalty.balance": "Your balance",
  "loyalty.howItWorks": "How it works",
  "loyalty.referTitle": "Refer a friend",
  "loyalty.referBody": "Give ₹100, get ₹100 when your friend's first order is delivered.",
  "loyalty.copyCode": "Copy my code",
  "loyalty.shareWhatsapp": "Share on WhatsApp",

  // ---------- support / policies ----------
  "support.title": "Support",
  "support.faq": "Frequently asked questions",
  "support.contactUs": "Contact us",
  "support.whatsappUs": "Message us on WhatsApp",
  "support.emailUs": "Email us",
  "policies.title": "Policies",
  "policies.lastUpdated": "Last updated",

  // ---------- vita ----------
  "vita.launcher": "Chat with Vita",
  "vita.title": "Vita — TruWe assistant",
  "vita.placeholder": "Ask about a product, ingredient or your order",
  "vita.send": "Send",
  "vita.thinking": "Vita is typing…",
  "vita.error": "Vita couldn't answer that right now. Please try again.",

  // ---------- footer ----------
  "footer.shop": "Shop",
  "footer.company": "Company",
  "footer.support": "Support",
  "footer.legal": "Legal",
  "footer.followUs": "Follow us",
  "footer.madeInIndia": "Made in India",
  "footer.rights": "All rights reserved.",

  // ---------- 404 / errors ----------
  "error.notFoundTitle": "Page not found",
  "error.notFoundBody": "The page you're looking for has moved or no longer exists.",
  "error.goHome": "Go to homepage",
  "error.crashTitle": "Something broke on our side",
  "error.crashBody": "Please refresh the page. If it keeps happening, message us on WhatsApp.",

  // ---------- homepage ----------
  "home.formulary.eyebrow": "The TruWe Formulary",
  "home.formulary.title1": "Three formulas.",
  "home.formulary.title2": "One precise system.",
  "home.formulary.body":
    "Precision-formulated actives at study-level doses. Liposomal and chelated forms chosen where they serve a real purpose, with a lab report for every released batch. Every ingredient is there for a reason.",
  "home.sku.buyNow": "Buy Now",
  "home.sku.explore": "Explore Formula",
  "home.sku.perPack": "per pack · 30 days",
  "home.sku.perDay": "₹{amount}/day",
  "home.eyebrow.liver": "Detox · Microbiome",
  "home.eyebrow.mag": "Recovery · Sleep · Performance",
  "home.eyebrow.ova": "Hormonal Balance · Cycle",
  "home.tagline.liver": "Wake up lighter. Liver + gut, daily.",
  "home.tagline.mag": "SLEEP · STRESS · RECOVERY",
  "home.tagline.ova": "CYCLE · HORMONES · METABOLIC SUPPORT",
  "home.copy.liver":
    "One formula, 5 layers of support: NAC for oxidative defence, Liposomal ALA for antioxidant renewal, Liposomal Curcumin for inflammatory balance, Liposomal Silymarin for Liver support and 10B Liver Supporting Probiotics Strains for gut balance.",
  "home.copy.mag":
    "5 complementary forms of magnesium—Aquamin®, Chloride, Aspartate, Gluconate and Liposomal Bisglycinate—chosen to support restful sleep, muscle function and daily recovery. Paired with zinc, copper, Active B6 (P-5-P) and Vitamin D3 to support magnesium utilisation, without melatonin or sedating herbs.",
  "home.copy.ova":
    "2,000 mg Myo-inositol and 50 mg D-chiro-inositol in a widely studied 40:1 ratio, paired with berberine HCl, Quatrefolic® active form of folate, magnesium, zinc and chromium to support cycle regularity, insulin signalling and metabolic wellbeing.",

  "home.cert.gmp": "GMP Certified",
  "home.cert.fssai": "FSSAI",
  "home.cert.usfda": "USFDA Approved Facility",
  "home.cert.lab": "Lab Tested",
  "home.cert.science": "Science Backed",
  "home.cert.clean": "Clean Formulations",

  "home.reviews.title": "What early users are noticing",

  "home.quiz.title1": "A 60-second quiz.",
  "home.quiz.title2": "A routine built for you.",
  "home.quiz.body":
    "Tell us how you're feeling: sleep, stress, hormones, gut. We'll match you to a science-backed routine you can add to cart in one tap.",
  "home.quiz.cta": "Start the quiz",
  "home.quiz.chip.sleep": "Better sleep",
  "home.quiz.chip.stress": "Less stress",
  "home.quiz.chip.hormones": "Hormonal balance",
  "home.quiz.chip.gut": "Gut & digestion",
  "home.quiz.chip.energy": "Energy",
  "home.quiz.chip.recovery": "Recovery",

  "home.why.eyebrow": "Why TruWe",
  "home.why.title1": "Because the supplement aisle",
  "home.why.title2": "stopped telling the truth.",
  "home.why.body1":
    "Doses got smaller while promises got bigger. Women's bodies were treated as a footnote. Routines became cluttered, and trust became rare.",
  "home.why.body2":
    "TruWe exists to make the next decade of Indian wellness honest again. Effective doses, a focused range, and labels you can verify.",
  "home.why.k1": "Effective doses.",
  "home.why.v1": "Every active at the dose the studies actually used. No fairy dust.",
  "home.why.k2": "Focused range.",
  "home.why.v2": "Fewer products. Each one earns its place in your routine.",
  "home.why.k3": "Honestly made.",
  "home.why.v3": "Third-party tested. Every batch. No exceptions.",
  "home.why.k4": "Built for the long game.",
  "home.why.v4": "Formulas you'll still believe in ten years from now.",

  // ---------- shop page (phase 2) ----------
  "shopPage.breadcrumbHome": "Home",
  "shopPage.breadcrumbShop": "Shop",
  "shopPage.eyebrow": "The TruWe formulary",
  "shopPage.h1": "Clinical wellness, built around what your body can absorb.",
  "shopPage.introBefore":
    "Three core formulas. One proprietary delivery system. Every TruWe supplement is built with",
  "shopPage.introAfter":
    "liposomal carriers for fat-soluble actives, paired with a proprietary phytonutrient blend formulated to support absorption. Research-backed doses, third-party lab tested, made in an FSSAI-licensed GMP facility in India.",
  "shopPage.quizCta": "Take the 60-second quiz",
  "shopPage.ingredientsCta": "See every ingredient",
  "shopPage.fallbackNotice":
    "Live pricing & checkout are temporarily unavailable. Browse the range below, checkout will return shortly.",
  "shopPage.shopByConcern": "Shop by concern",
  "shopPage.noMatch": "No products match this concern yet,",
  "shopPage.showAll": "show all",
  "shopPage.concern.all": "All",
  "shopPage.concern.sleep": "Sleep & stress",
  "shopPage.concern.hormones": "PCOS & hormones",
  "shopPage.concern.gut": "Liver & gut",
  "shopPage.blurb.all": "The complete TruWe adult formulary.",
  "shopPage.blurb.sleep":
    "5-form magnesium built for deep sleep, calm nerves and muscle recovery.",
  "shopPage.blurb.hormones":
    "Gold-standard 40:1 inositol with Quatrefolic® folate for cycle regularity and hormonal acne.",
  "shopPage.blurb.gut":
    "Liposomal curcumin, silymarin, ALA and NAC for daily detox and bloating.",
  "shopPage.byNeedTitle": "Shop by need",
  "shopPage.byNeedBody":
    "Explore curated collection pages, each one covers what to look for, which ingredients matter, and the safety notes that go with them.",
  "shopPage.byGoalTitle": "Shop by what you actually want to fix",
  "shopPage.byGoalBody":
    "Not sure where to start? Pick the result you care about, we'll point you at the right formula.",
  "shopPage.formularyTitle": "Inside the formulary",
  "shopPage.readFull": "Read the full formulation →",
  "shopPage.learnTitle": "Learn before you buy",
  "shopPage.learnBody": "Plain-English science from our editorial desk, no hype, no fluff.",
  "shopPage.faqTitle": "Frequently asked",

  // ---------- cart drawer (phase 2) ----------
  "cartDrawer.title": "Your routine ({count})",
  "cartDrawer.description":
    "Review the products in your cart, adjust quantities, and proceed to checkout.",
  "cartDrawer.empty": "Your cart is empty.",
  "cartDrawer.bundleApplied": "Bundle savings applied",
  "cartDrawer.bundlePrompt": "Add a duo or trio to unlock bundle pricing",
  "cartDrawer.bundleHint":
    "Bundle discount applies automatically when the full protocol is in your cart.",
  "cartDrawer.packPricingApplied": "Pack pricing applied",
  "cartDrawer.haveCode": "Have a code?",
  "cartDrawer.apply": "Apply",
  "cartDrawer.couponPrepaidOnly": "Applies on prepaid only. Switch to prepaid to use this code.",
  "cartDrawer.couponTerms": "Terms: single use.",
  "cartDrawer.couponTermsLong": "Terms: single use, one code per order.",
  "cartDrawer.couponExpiredTitle": "This coupon expired",
  "cartDrawer.couponExpiredBody": "Codes are valid for 30 days from when they're issued.",
  "cartDrawer.offApplied": "off applied",
  "cartDrawer.removeCoupon": "Remove coupon",
  "cartDrawer.priceDetails": "Price details",
  "cartDrawer.subtotalItems": "Subtotal ({count} {unit})",
  "cartDrawer.item": "item",
  "cartDrawer.items": "items",
  "cartDrawer.bundleSavings": "Bundle savings",
  "cartDrawer.packPricing": "Pack pricing",
  "cartDrawer.coupon": "Coupon",
  "cartDrawer.codFee": "Advance paid online now (no COD fee)",
  "cartDrawer.cartTotal": "Cart total",
  "cartDrawer.prepaidBenefit": "Estimated prepaid benefit ({percent}%)",
  "cartDrawer.afterBenefits": "Estimated after benefits",
  "cartDrawer.prepaidNote":
    "5% prepaid benefit is applied at the Razorpay payment step (UPI, Cards, Net Banking, Wallets).",
  "cartDrawer.codNote":
    "₹{fee} is paid online now to confirm your address, and it is deducted from your total. The balance is paid in cash or UPI to the courier on delivery. Zero COD handling fee.",

  "cartDrawer.taxNote": "Prices inclusive of all taxes. Shipping calculated at checkout.",
  "cartDrawer.freeShippingApplied": "Free shipping applied",
  "cartDrawer.freeShippingRemaining": "Add ₹{amount} more for free shipping",
  "cartDrawer.payNow": "Pay securely now",
  "cartDrawer.placeCod": "Place COD order",
  "cartDrawer.soldOut": "Sold out",
  "cartDrawer.secureCheckout": "secure checkout",
  "cartDrawer.savesAmount": "saves ₹{amount}",
  "cartDrawer.switchPrepaid": "switch to prepaid to save more",

  // ---------- pack selector (phase 2) ----------
  "pack.selectTitle": "Pack Size Select",
  "pack.whatYouPayFor": "What you are paying for",
  "pack.whatYouPayForTitle": "What you're really paying for",
  "pack.whatYouPayForBody1":
    "The value is not in a longer ingredient list. It is in meaningful, disclosed doses; carefully selected liposomal and chelated forms where they serve a real purpose; and independent verification of every released batch.",
  "pack.whatYouPayForBody2":
    "You can see what is inside, understand why each form was chosen, and review the research behind those decisions.",
  "pack.whatYouPayForBody3": "That is the TruWe standard: precision you can verify.",
  "pack.save": "Save ₹{amount}",
  "pack.perDay": "Only ₹{amount}/day",
  "pack.noCostEmi": "No-cost EMI",
  "pack.badge.starter": "STARTER",
  "pack.badge.popular": "MOST POPULAR",
  "pack.badge.best": "BEST RESULTS",
  "pack.supply30": "30-day protocol",
  "pack.supply60": "60-day protocol",
  "pack.supply90": "90-day protocol",

  // ---------- collection pages ----------
  "collection.breadcrumbHome": "Home",
  "collection.breadcrumbShop": "Shop",
  "collection.recommended": "Recommended from TruWe",
  "collection.viewFormulation": "View the formulation",
  "collection.exploreRange": "Explore the range",
  "collection.visit": "Visit {name}",
  "collection.keyIngredients": "Key ingredients in this collection",
  "collection.ingredientsNote":
    "Every active is documented on our ingredients & research page, with dose, form and citations.",
  "collection.ingredientsLink": "ingredients & research page",
  "collection.details": "Details",
  "collection.safetyHeading": "Safety & clinician guidance",
  "collection.safetyNote":
    "These are food supplements, not medicines. Nothing on this page is intended to diagnose, treat, cure or prevent any disease.",
  "collection.library": "From the TruWe library",
  "collection.faqs": "Frequently asked",
  "collection.others": "Other TruWe collections",

  // ---------- product page body ----------
  "pdp.howToUse": "How to use",
  "pdp.routineNote":
    "Take it at the same time each day. Consistency matters more than the exact hour.",

  // ---------- quiz / routine ----------
  "quiz.whyThis": "Why this is your match",
  "quiz.whatToExpect": "What to expect",
  "quiz.day": "Day {n}",
} as const;



export const en = { ...baseEn, ...SECTIONS_EN };

export type TranslationKey = keyof typeof en;

/**
 * Hinglish — reviewed, human-written. Partial by design: anything absent
 * renders the full English sentence above.
 */
